import React, { useEffect, useRef, useState, useCallback } from "react";
import { Film, Download, Link as LinkIcon, Share2, Square, Clapperboard, Globe2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const CLIP_SECONDS = 15;
const CINEMA_SECONDS = 10;

// Records ~15s of live gameplay (canvas video + game audio) and turns it into a
// shareable, sound-on social clip. Drives the runner via the GameCanvas ref.
export default function ShareClipButton({ canvasRef, game, gameId, user, finishSignal = 0, robotScore = null }) {
  const [phase, setPhase] = useState("idle"); // idle | recording | ready
  const [total, setTotal] = useState(CLIP_SECONDS);
  const [elapsed, setElapsed] = useState(0);
  const [open, setOpen] = useState(false);
  const [clip, setClip] = useState(null); // { url, mime, size }
  const [caption, setCaption] = useState("");
  const [attachScore, setAttachScore] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);
  const activeRef = useRef(false);
  const tickRef = useRef(null);
  const urlRef = useRef(null);

  const playUrl = `${window.location.origin}/g/${gameId}`;
  const shareText = `🎮 Check out "${game?.title || "my game"}" on GameView Studio — built & played with AI!`;
  const ext = clip && clip.mime && clip.mime.indexOf("mp4") >= 0 ? "mp4" : "webm";
  const slug = (game?.title || "gameview").replace(/[^a-z0-9]+/gi, "-").toLowerCase();

  const clearTick = () => { if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; } };
  const revoke = () => { if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; } };

  useEffect(() => {
    const handler = (ev) => {
      const d = ev.data || {};
      if (!activeRef.current) return;
      if (d.type === "record-started") {
        setTotal(d.total || CLIP_SECONDS);
        setElapsed(0);
        setPhase("recording");
        clearTick();
        tickRef.current = setInterval(() => setElapsed((e) => e + 0.25), 250);
      } else if (d.type === "record-done") {
        clearTick();
        activeRef.current = false;
        if (!d.blob || !d.size) { setPhase("idle"); toast.error("The clip came out empty — try again while playing."); return; }
        revoke();
        const url = URL.createObjectURL(d.blob);
        urlRef.current = url;
        setClip({ url, mime: d.mime || "video/webm", size: d.size });
        setPhase("ready");
        setOpen(true);
      } else if (d.type === "record-error") {
        clearTick();
        activeRef.current = false;
        setPhase("idle");
        toast.error(d.message || "Couldn't record the clip.");
      }
    };
    window.addEventListener("message", handler);
    return () => { window.removeEventListener("message", handler); clearTick(); revoke(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startRecord = useCallback(() => {
    if (!canvasRef?.current?.startRecording) { toast.error("Start playing first, then record."); return; }
    revoke(); setClip(null); setPublished(false); setCaption("");
    activeRef.current = true;
    setPhase("recording"); setElapsed(0);
    toast.message("Recording… play your best moment! 🎬");
    canvasRef.current.startRecording(CLIP_SECONDS);
  }, [canvasRef]);

  const stopRecord = () => { if (canvasRef?.current?.stopRecording) canvasRef.current.stopRecording(); };

  // Cinematic clip: flip the runner to the Cinematic graphics tier (richer lighting/bloom/shadows),
  // give it a beat to rebuild, then record a short, gorgeous highlight reel.
  const startCinematic = useCallback(() => {
    if (!canvasRef?.current?.startRecording) { toast.error("Start playing first, then record."); return; }
    revoke(); setClip(null); setPublished(false); setCaption("");
    try { canvasRef.current.setGfx?.("cinema"); } catch {}
    toast.message("🎬 Cinematic mode on — capturing your best moment!");
    setTimeout(() => {
      activeRef.current = true;
      setPhase("arming"); setElapsed(0);
      if (canvasRef.current.highlightClip) canvasRef.current.highlightClip(CINEMA_SECONDS);
      else canvasRef.current.startRecording(CINEMA_SECONDS);
    }, 1400);
  }, [canvasRef]);

  const nudgedRef = useRef(false);
  useEffect(() => {
    if (!finishSignal || nudgedRef.current) return;
    if (phase !== "idle" || published || open) return;
    nudgedRef.current = true;
    toast("🎬 Nice run! Turn it into a shareable clip", {
      description: "Record your next attempt and post it to the community feed.",
      action: { label: "Record clip", onClick: () => startRecord() },
      duration: 9000,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishSignal]);

  const publishClip = async () => {
    if (!clip || publishing) return;
    if (!user) { toast.error("Log in to publish your clip to the community."); return; }
    setPublishing(true);
    try {
      const resp = await fetch(clip.url);
      const blob = await resp.blob();
      const fd = new FormData();
      fd.append("game_id", gameId);
      const scorePrefix = (robotScore != null && attachScore) ? `🤖 Scored ${robotScore}/100 · ` : "";
      fd.append("caption", (scorePrefix + (caption || "")).slice(0, 200));
      fd.append("clip", blob, `${slug}-gameview.${ext}`);
      await api.post("/clips", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPublished(true);
      toast.success("Published to Community Clips! 🎬");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't publish the clip.");
    } finally { setPublishing(false); }
  };

  const download = () => {
    if (!clip) return;
    const a = document.createElement("a");
    a.href = clip.url; a.download = `${slug}-gameview.${ext}`;
    a.click();
    toast.success("Clip downloaded — post it anywhere!");
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(`${shareText} ${playUrl}`); toast.success("Caption + link copied!"); }
    catch { toast.error("Couldn't copy."); }
  };

  const nativeShare = async () => {
    if (!clip) return;
    try {
      const resp = await fetch(clip.url);
      const blob = await resp.blob();
      const file = new File([blob], `${slug}-gameview.${ext}`, { type: clip.mime });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: game?.title, text: shareText });
      } else {
        await navigator.share({ title: game?.title, text: shareText, url: playUrl });
      }
    } catch { /* cancelled / unsupported */ }
  };

  const SOCIALS = [
    { key: "x", label: "X", color: "#000000", href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(playUrl)}` },
    { key: "reddit", label: "Reddit", color: "#ff4500", href: `https://www.reddit.com/submit?url=${encodeURIComponent(playUrl)}&title=${encodeURIComponent(shareText)}` },
    { key: "whatsapp", label: "WhatsApp", color: "#25d366", href: `https://wa.me/?text=${encodeURIComponent(shareText + " " + playUrl)}` },
    { key: "telegram", label: "Telegram", color: "#229ed9", href: `https://t.me/share/url?url=${encodeURIComponent(playUrl)}&text=${encodeURIComponent(shareText)}` },
  ];

  const pct = Math.min(100, Math.round((elapsed / total) * 100));
  const sizeLabel = clip
    ? (clip.size >= 1024 * 1024 ? `${(clip.size / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(clip.size / 1024))} KB`)
    : "0 KB";

  return (
    <>
      {phase === "recording" || phase === "arming" ? (
        <div className="flex items-center gap-2" data-testid="clip-recording-indicator">
          {phase === "arming" ? (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/15 border border-violet-500/40 text-violet-300 text-xs font-semibold" data-testid="clip-arming-indicator">
              <span className="w-2 h-2 rounded-full bg-violet-400 animate-ping" /> 🎬 Waiting for the action…
            </div>
          ) : (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-500/15 border border-rose-500/40 text-rose-300 text-xs font-semibold">
              <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
              REC {Math.ceil(total - elapsed)}s
              <span className="w-16 h-1 rounded-full bg-rose-500/25 overflow-hidden"><span className="block h-full bg-rose-400 transition-all" style={{ width: `${pct}%` }} /></span>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={stopRecord} className="rounded-full gap-1.5" data-testid="record-clip-stop">
            <Square className="w-3.5 h-3.5 fill-current" /> Stop
          </Button>
        </div>
      ) : (
        <>
          <Button variant="outline" size="sm" onClick={startRecord}
            className="rounded-full gap-1.5 border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10"
            data-testid="record-clip-btn">
            <Film className="w-4 h-4" /> Record clip
          </Button>
          <Button variant="outline" size="sm" onClick={startCinematic}
            className="rounded-full gap-1.5 border-violet-500/40 text-violet-300 hover:bg-violet-500/10"
            data-testid="cinematic-clip-btn" title="Record a Cinematic-mode highlight reel">
            <Clapperboard className="w-4 h-4" /> Cinematic clip
          </Button>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="clip-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Clapperboard className="w-5 h-5 text-fuchsia-400" /> Your gameplay clip</DialogTitle>
            <DialogDescription>A {Math.round(total)}s sound-on clip of your run. Download it and post to any feed — or share straight from your phone.</DialogDescription>
          </DialogHeader>
          {clip && (
            <video src={clip.url} controls autoPlay loop muted playsInline
              onLoadedMetadata={(e) => { try { if (e.currentTarget.paused) e.currentTarget.currentTime = 0.05; } catch {} }}
              className="w-full rounded-xl border border-[hsl(var(--border))] bg-black" data-testid="clip-video" style={{ aspectRatio: "16/9" }} />
          )}
          <div className="flex flex-wrap gap-2">
            <Button onClick={download} className="rounded-full gap-1.5 flex-1 bg-gradient-to-r from-fuchsia-500 to-violet-500" data-testid="clip-download"><Download className="w-4 h-4" /> Download ({sizeLabel})</Button>
            <Button variant="outline" onClick={copyLink} className="rounded-full gap-1.5 flex-1" data-testid="clip-copy-link"><LinkIcon className="w-4 h-4" /> Copy caption + link</Button>
            {typeof navigator !== "undefined" && navigator.share && (
              <Button variant="outline" onClick={nativeShare} className="rounded-full gap-1.5 flex-1" data-testid="clip-native"><Share2 className="w-4 h-4" /> Share…</Button>
            )}
          </div>
          <div className="rounded-xl border border-fuchsia-500/30 bg-fuchsia-500/5 p-3 space-y-2" data-testid="clip-publish-panel">
            <div className="text-xs font-semibold flex items-center gap-1.5"><Globe2 className="w-3.5 h-3.5 text-fuchsia-400" /> Publish to Community Clips</div>
            {published ? (
              <p className="text-[12px] text-emerald-400 font-semibold" data-testid="clip-published">✓ Published — find it on the Community Hub!</p>
            ) : user ? (
              <>
                {robotScore != null && (
                  <button type="button" onClick={() => setAttachScore((v) => !v)} data-testid="clip-attach-score"
                    className={`w-full flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[12px] font-semibold transition-colors ${attachScore ? "border-violet-500/50 bg-violet-500/10 text-violet-300" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}>
                    <span className={`w-4 h-4 rounded grid place-items-center text-[10px] ${attachScore ? "bg-violet-500 text-white" : "border border-current"}`}>{attachScore ? "✓" : ""}</span>
                    🤖 Attach my robot score — <b>{robotScore}/100</b>
                  </button>
                )}
                <Input value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={200}
                  placeholder="Add a caption (optional)…" className="h-8 text-sm" data-testid="clip-caption" />
                <Button onClick={publishClip} disabled={publishing} size="sm"
                  className="w-full rounded-full gap-1.5 bg-gradient-to-r from-fuchsia-500 to-violet-500" data-testid="clip-publish-btn">
                  {publishing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Publishing…</> : <><Globe2 className="w-3.5 h-3.5" /> Publish to Community</>}
                </Button>
              </>
            ) : (
              <p className="text-[12px] text-[hsl(var(--muted-foreground))]">Log in to publish this clip to the public feed.</p>
            )}
          </div>
          <div>
            <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-2">Post the link to</div>
            <div className="flex flex-wrap gap-2">
              {SOCIALS.map((s) => (
                <button key={s.key} onClick={() => window.open(s.href, "_blank", "noopener,noreferrer")} data-testid={`clip-social-${s.key}`}
                  className="px-3.5 py-2 rounded-full text-xs font-semibold text-white transition-transform hover:-translate-y-0.5"
                  style={{ backgroundColor: s.color }}>
                  {s.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2">Tip: download the clip, then attach it to your post for the full sound-on experience.</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
